import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from "@angular/core";
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { CameraComponent } from "src/app/annotation/camera/camera.component";
import { TokenService } from "src/app/auth/token.service";
import { FormValidationService } from "src/app/core/survey/shared/services/formValidation.service";
import { SurveyType } from "src/app/core/survey/shared/services/photo.service";
import { RoleEnum } from "src/app/shared/Models/enums/RoleEnum";
import { InsideSurveyService } from "src/app/shared/services/inside-survey.service";
import { SurveyPhotoTypeEnum } from "../../../../../../../shared/Models/enums/SurveyPhotoTypeEnum";
import { InsideSurvey } from "../../../../../../../shared/Models/read";
import { ToastService } from "../../../../../../../shared/toast.service";
import {
  InsideSurveyInsideInfo,
  INSIDE_SURVEY_INSIDE_TECHNICAL_INFO,
} from "../../../../config/inside-survey.magic-strings";
import { InsideIntroduction } from "../../../../models/insideIntroduction.enum";
import { Quadrant } from "../../../../models/quadrant.enum";
import { additionalQuestionFields } from "../inside-survey-inside-technical-info.component";

@Component({
  selector: "app-general-technical-info",
  templateUrl: "./general-technical-info.component.html",
  styleUrls: ["./general-technical-info.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GeneralTechnicalInfoComponent),
      multi: true,
    },
  ],
})
export class GeneralTechnicalInfoComponent
  implements OnInit, ControlValueAccessor, OnDestroy
{
  @Input() pictures: {
    generalTechnicalInfoPhotos: [];
    electricityInTelcoRoomPhotos: [];
    roomAvailableForNewEquipmentPhotos: [];
  };
  @Input() isAuthCanWrite: boolean;
  @Input() survey: InsideSurvey;
  @Input() cameraLayer: HTMLDivElement;
  @Input() camera: CameraComponent;
  generalTechnicalInfoForm: FormGroup = new FormGroup({});
  technicalInfoQuestionsBoolOptions = [
    { displayName: $localize`:@@general.yes:Yes`, value: true },
    { displayName: $localize`:@@general.no:No`, value: false },
  ];
  quadrantType = [
    {
      value: Quadrant.A,
      label: "A",
    },
    {
      value: Quadrant.B,
      label: "B",
    },
    {
      value: Quadrant.C,
      label: "C",
    },
    {
      value: Quadrant.D,
      label: "D",
    },
    {
      value: Quadrant.F,
      label: "F",
    },
    {
      value: Quadrant.XD,
      label: "XD",
    },
    {
      value: Quadrant.XC,
      label: "XC",
    },
    {
      value: Quadrant.XA,
      label: "XA",
    },
    {
      value: Quadrant.E,
      label: "E",
    },
  ];
  technicalInfoQuestionIntroductionOptions = Object.keys(InsideIntroduction)
    .filter((i) => isNaN(+i))
    .map((i) => {
      return { displayName: i, value: InsideIntroduction[i] };
    });
  technicalInfoQuestionQuadrantOptions = Object.keys(Quadrant)
    .filter((i) => isNaN(+i))
    .map((i) => {
      return { displayName: i, value: Quadrant[i] };
    });
  formChanges: Subject<Partial<InsideSurvey>> = new Subject<
    Partial<InsideSurvey>
  >();
  isDisabled: boolean = false;
  surveyPhotoTypes = SurveyPhotoTypeEnum;
  @Input() viewerLayer: HTMLDivElement;

  @ViewChildren("fileInput")
  fileInputs: QueryList<ElementRef<HTMLInputElement>>;
  additionalQuestionsVisible: boolean;

  surveyType = SurveyType;
  surveyPhotoType = SurveyPhotoTypeEnum;

  generalPhotoRemarkFieldName = "generalTechnicalInfoPhotoRemark";
  electricityInTelcoRoomPhotoRemarkFieldName =
    "electricityInTelcoRoomPhotoRemark";
  roomAvailableForNewEquipmentPhotoRemarkFieldName =
    "roomAvailableForNewEquipmentPhotoRemark";

  insideSurveyInsideInfo = InsideSurveyInsideInfo;

  constructor(
    private fb: FormBuilder,
    private toastService: ToastService,
    private readonly service: InsideSurveyService,
    private tokenService: TokenService,
    private formValidationService: FormValidationService
  ) {}

  ngOnInit() {
    this.buildForms();
  }

  getErrors(subTitle?: string, field?: string) {
    return this.formValidationService.getErrors(
      ...[INSIDE_SURVEY_INSIDE_TECHNICAL_INFO, subTitle, field].filter(
        (x) => !!x
      )
    );
  }

  private buildForms() {
    this.generalTechnicalInfoForm = this.fb.group({
      introduction: [{ value: "", disabled: !this.isAuthCanWrite }, []],
      quadrant: [{ value: "", disabled: false }, []],
      horizontalTechnicalShaft: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      verticalTechnicalShaft: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      electricityInTelcoRoom: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      roomAvailableForFutureIntroBox: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      otherTelcoRemarks: [[]],
      generalTechnicalInfoPhotoRemark: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      electricityInTelcoRoomPhotoRemark: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      roomAvailableForNewEquipmentPhotoRemark: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      [additionalQuestionFields.nrOfGutters]: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      [additionalQuestionFields.nrOf90DegreeCorners]: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      [additionalQuestionFields.nrOfInsideOutsideCorners]: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      [additionalQuestionFields.nrOfEndKitsForGutters]: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      [additionalQuestionFields.nrOfHorizontalDrilling]: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      [additionalQuestionFields.nrOfVerticalDrilling]: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      [additionalQuestionFields.nrOfSealingKitsForDrillingHoles]: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
      [additionalQuestionFields.sellingHigherThan3and5m]: [
        { value: "", disabled: !this.isAuthCanWrite },
        [],
      ],
    });

    this.generalTechnicalInfoForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        this.checkAdditionalQuestionsVisibility(val);
        this.formChanges.next(val);
      });
  }

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  formatInt(e) {
    try {
      e.target.value = Number.parseInt(e.target.value, 10);
    } catch {
      e.target.value = undefined;
    }
  }

  get verticalTechnicalShaft(): Boolean {
    return this.generalTechnicalInfoForm.get("verticalTechnicalShaft").value;
  }
  get isElectricityInTelcoRoom(): Boolean {
    return this.generalTechnicalInfoForm.get("electricityInTelcoRoom").value;
  }

  get isRoomAvailable(): Boolean {
    return this.generalTechnicalInfoForm.get("roomAvailableForFutureIntroBox")
      .value;
  }

  writeValue(obj: any): void {
    this.generalTechnicalInfoForm.patchValue({ ...obj });
  }
  registerOnChange(fn: any): void {
    this.formChanges.pipe(takeUntil(this.destroy$)).subscribe(fn);
  }
  registerOnTouched(fn: any): void {
    this.onTouched();
  }
  onTouched = () => null;

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  checkAdditionalQuestionsVisibility(obj: any) {
    if (this.service.areAdditionalGeneralQuestionsVisible(obj))
      this.additionalQuestionsVisible = true;
    else this.additionalQuestionsVisible = false;
  }

  getCurrentQuadrant() {
    return this.generalTechnicalInfoForm.get("quadrant").value;
  }

  onChangeQuadrantValue(value: Quadrant) {
    if (
      (value == Quadrant.E ||
        this.generalTechnicalInfoForm.get("quadrant").value == Quadrant.E ||
        value == Quadrant.B ||
        this.generalTechnicalInfoForm.get("quadrant").value == Quadrant.B) &&
      !this.tokenService.hasSomeRole([RoleEnum.Admin, RoleEnum.Validator])
    ) {
      this.toastService.showWarnToast(
        $localize`:@@validation.quadrantchangeforbidden:You are not allowed to select/change B- or E-quadrant`,
        ""
      );
      return;
    }
    this.generalTechnicalInfoForm.controls.quadrant.setValue(value);
  }
}
